import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Event as EventIcon } from '@mui/icons-material';
import { Website } from 'types/website.types';
import { EventItem, PaginatedEventsResponse, EventDate, SetEventDateOccurrence, WeekdayCode, RecurringByWeekdayNth } from 'types/events.types';
import { getWebsitesByPublisher } from 'api/apiClient';
import { createEvent, deleteEvent, listEvents, listEventsByWebsite, updateEvent } from 'features/events/api';
import ConfirmDialog from 'features/publishers/components/common/ConfirmDialog';
import EventForm from './EventForm';

interface EventsTableProps {
  publisherId?: string;
}

const DEFAULT_LIMIT = 100;

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const weekdayShortMap: Record<WeekdayCode, string> = {
  MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun'
};

const formatDateOnly = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(dt);
};

const formatTimeOnly = (t?: string) => t || '';

const formatSingle = (d: any) => {
  const start = d.start ? new Date(d.start) : undefined;
  const end = d.end ? new Date(d.end) : undefined;
  if (!start) return '';
  const dateFmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(start);
  const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
  const startTime = timeFmt.format(start);
  if (end) {
    const sameDay = start.toDateString() === end.toDateString();
    const endTime = timeFmt.format(end);
    return sameDay ? `${dateFmt} ${startTime} – ${endTime}` : `${dateFmt} ${startTime} → ${new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(end)} ${endTime}`;
  }
  return `${dateFmt} ${startTime}`;
};

const formatSpan = (d: any) => {
  const range = `${formatDateOnly(d.startDate)} – ${formatDateOnly(d.endDate)}`;
  if (d.startTime || d.endTime) {
    const times = `${d.startTime || ''}${d.startTime && d.endTime ? ' – ' : ''}${d.endTime || ''}`;
    return `${range} • Daily ${times}`.trim();
  }
  return range;
};

const formatSet = (d: any) => {
  const occs: SetEventDateOccurrence[] = Array.isArray(d.occurrences) ? d.occurrences : [];
  if (!occs.length) return '';
  const first3 = occs.slice(0, 3).map(o => `${formatDateOnly(o.date)}${o.startTime ? ` ${o.startTime}` : ''}${o.endTime ? ` – ${o.endTime}` : ''}`);
  const extra = occs.length > 3 ? `, +${occs.length - 3} more` : '';
  return first3.join(', ') + extra;
};

const formatRecurring = (d: any) => {
  const rule = d.rule || {};
  const freq = rule.freq;
  const until = rule.until ? ` until ${formatDateOnly(rule.until)}` : '';
  const timePart = rule.startTime || rule.endTime ? ` ${rule.startTime || ''}${rule.startTime && rule.endTime ? ' – ' : ''}${rule.endTime || ''}` : '';
  let by = '';
  if (Array.isArray(rule.byWeekday) && rule.byWeekday.length) {
    const parts: string[] = [];
    rule.byWeekday.forEach((w: WeekdayCode | RecurringByWeekdayNth) => {
      if (typeof w === 'string') parts.push(weekdayShortMap[w]);
      else parts.push(`${w.nth.map(ordinal).join(', ')} ${weekdayShortMap[w.weekday]}`);
    });
    by = parts.join(', ');
  }
  if (freq === 'DAILY') return `Daily${timePart}${until}`.trim();
  if (freq === 'WEEKLY') return `Weekly on ${by || '—'}${timePart}${until}`.trim();
  if (freq === 'MONTHLY') return `Monthly on ${by || '—'}${timePart}${until}`.trim();
  if (freq === 'YEARLY') return `Yearly${by ? ' on ' + by : ''}${timePart}${until}`.trim();
  return `Recurring${timePart}${until}`.trim();
};

const formatEventDate = (date?: EventDate): { label: string; detail?: string } => {
  if (!date) return { label: '' };
  const anyDate: any = date;
  if (anyDate.kind === 'single') return { label: formatSingle(anyDate) };
  if (anyDate.kind === 'span') return { label: formatSpan(anyDate) };
  if (anyDate.kind === 'set') return { label: formatSet(anyDate), detail: JSON.stringify(anyDate.occurrences) };
  if (anyDate.kind === 'recurring') return { label: formatRecurring(anyDate) };
  // legacy
  if (anyDate.when) return { label: anyDate.when };
  if (anyDate.start_date) return { label: formatSingle({ start: anyDate.start_date }) };
  return { label: '' };
};

const EventsTable: React.FC<EventsTableProps> = ({ publisherId }) => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<string>('ALL');

  const [items, setItems] = useState<EventItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>(undefined);

  // Pagination (client-side)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search
  const [search, setSearch] = useState('');

  // Form state
  const [openForm, setOpenForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventItem | undefined>(undefined);

  // Delete state
  const [openDelete, setOpenDelete] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<EventItem | undefined>(undefined);

  const filteredItems = useMemo(() => {
    let data = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(e =>
        e.title?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.venue?.name?.toLowerCase().includes(q)
      );
    }
    return data;
  }, [items, search]);

  const displayed = useMemo(() => (
    filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  ), [filteredItems, page, rowsPerPage]);

  const loadWebsites = async () => {
    if (!publisherId) return;
    try {
      const data = await getWebsitesByPublisher(publisherId);
      setWebsites(data || []);
    } catch (e) {
      console.error('Failed to load websites', e);
    }
  };

  const resetAndLoad = async () => {
    setItems([]);
    setLastEvaluatedKey(undefined);
    setPage(0);
    await loadEvents(true);
  };

  const loadEvents = async (initial = false) => {
    setIsLoading(true);
    setError('');
    try {
      let resp: PaginatedEventsResponse;
      if (selectedWebsite !== 'ALL' && selectedWebsite) {
        resp = await listEventsByWebsite(selectedWebsite, { maxEvents: DEFAULT_LIMIT, lastEvaluatedKey: initial ? undefined : lastEvaluatedKey });
      } else {
        resp = await listEvents({ limit: DEFAULT_LIMIT, lastEvaluatedKey: initial ? undefined : lastEvaluatedKey });
      }
      setItems(prev => [...prev, ...(resp.items || [])]);
      setLastEvaluatedKey(resp.lastEvaluatedKey);
    } catch (e: any) {
      console.error('Failed to load events', e);
      setError(e?.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWebsites();
  }, [publisherId]);

  useEffect(() => {
    resetAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWebsite]);

  const handleCreate = () => {
    setEditingEvent(undefined);
    setOpenForm(true);
  };

  const handleEdit = (ev: EventItem) => {
    setEditingEvent(ev);
    setOpenForm(true);
  };

  const handleSubmit = async (data: Partial<EventItem>) => {
    if (editingEvent) {
      const updated = await updateEvent(editingEvent.id, data);
      setItems(prev => prev.map(i => (i.id === editingEvent.id ? updated : i)));
    } else {
      const created = await createEvent(data);
      setItems(prev => [created, ...prev]);
    }
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      await deleteEvent(eventToDelete.id);
      setItems(prev => prev.filter(i => i.id !== eventToDelete.id));
    } finally {
      setOpenDelete(false);
      setEventToDelete(undefined);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <EventIcon color="primary" />
          <Typography variant="h6" sx={{ mr: 'auto' }}>Events</Typography>
          <TextField
            size="small"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select size="small" value={selectedWebsite} onChange={(e) => setSelectedWebsite(String(e.target.value))}>
            <MenuItem value="ALL">All Websites</MenuItem>
            {websites.map(w => {
              const value = w.id.startsWith('WEBSITE#') ? w.id : `WEBSITE#${w.id}`;
              return <MenuItem key={value} value={value}>{w.name}</MenuItem>;
            })}
          </Select>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>New Event</Button>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Website</TableCell>
                <TableCell>Link</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : displayed.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">No events found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayed.map((ev) => {
                  const dateInfo = formatEventDate(ev.date);
                  return (
                    <TableRow key={ev.id} hover>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{ev.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{ev.address?.join(', ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {typeof (ev.date as any)?.kind === 'string' && (
                            <Chip size="small" label={(ev.date as any).kind} variant="outlined" />
                          )}
                          <Tooltip title={dateInfo.detail || ''} disableHoverListener={!dateInfo.detail}>
                            <Typography variant="body2">{dateInfo.label}</Typography>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {ev.venue?.name ? <Chip size="small" label={ev.venue.name} /> : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ev.websitePK?.replace('WEBSITE#', '')}</Typography>
                      </TableCell>
                      <TableCell>
                        {ev.link ? (
                          <Link href={ev.link} target="_blank" rel="noopener noreferrer">Open</Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" onClick={() => handleEdit(ev)}><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => { setEventToDelete(ev); setOpenDelete(true); }}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {lastEvaluatedKey && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Button onClick={() => loadEvents(false)} disabled={isLoading} sx={{ my: 1 }}>
                      {isLoading ? 'Loading…' : 'Load More'}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, 50]}
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <EventForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleSubmit}
        isEdit={!!editingEvent}
        event={editingEvent}
        websites={websites}
      />

      <ConfirmDialog
        open={openDelete}
        title="Delete Event?"
        message={`Are you sure you want to delete "${eventToDelete?.title}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setOpenDelete(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {error && (
        <Box sx={{ mt: 2 }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default EventsTable;
